// The flyover is opened through these window functions (from the hike, recording and planned-trail
// pages and the map apps). FlyoverPreview.vue defines the real ones when it mounts.
export const FLYOVER_LAUNCHERS = [
  'showMapModalForTrail',
  'showMapModalForPlannedTrail',
  'showMapModalForRecordedTrail',
  'showMapModalForCoordinates',
] as const;

type Launcher = (...args: unknown[]) => unknown;

export interface LazyLauncherOptions {
  /** Called when a launcher has to wait for the app to load; returns the function that ends the wait. */
  onWait?: () => () => void;
}

export interface LazyLaunchers {
  /** Starts loading the app ahead of a launch (e.g. on hover). Failures are left for the launch to retry. */
  preload: () => Promise<void>;
}

/**
 * Stands in a stub for each launcher that loads the real app on first call and then hands the call
 * over to the launcher it defined. The flyover pulls in Mapbox GL (1.5 MB) and the base-map dialog,
 * which every page importing it used to download and run up front for a modal most visitors never open.
 *
 * Only one load runs at a time; a failed load can be retried by calling a launcher again.
 */
export function installLazyLaunchers(
  target: Record<string, unknown>,
  names: readonly string[],
  load: () => Promise<void>,
  options: LazyLauncherOptions = {}
): LazyLaunchers {
  let loading: Promise<void> | null = null;
  const ensureLoaded = () =>
    (loading ??= load().catch((error) => {
      loading = null;
      throw error;
    }));

  names.forEach((name) => {
    const stub: Launcher = (...args) => {
      const endWait = options.onWait?.();

      return ensureLoaded()
        .then(
          () => {
            const real = target[name];
            if (typeof real !== 'function' || real === stub) {
              console.error(`[flyover] ${name} was not defined by the flyover app`);
              return undefined;
            }
            return (real as Launcher)(...args);
          },
          (error) => {
            console.error('[flyover] failed to load the flyover app', error);
          }
        )
        .finally(() => endWait?.());
    };
    target[name] = stub;
  });

  return { preload: () => ensureLoaded().catch(() => undefined) };
}
